import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, BookBookmark, ChatCircleDots, ClockCounterClockwise, NotePencil, Paperclip,
  Scroll, Trash, UploadSimple,
} from "@phosphor-icons/react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/shared";
import { CharterBuilder } from "@/pages/viveka/CharterBuilder";
import { CharterDiscussion } from "@/pages/viveka/CharterDiscussion";
import { toast } from "sonner";

const TABS = [
  { key: "charter", label: "The charter", icon: Scroll },
  { key: "build", label: "Generate / edit", icon: NotePencil },
  { key: "versions", label: "Versions", icon: ClockCounterClockwise },
  { key: "discussion", label: "Family discussion", icon: ChatCircleDots },
];

const CharterView = ({ draft }) => {
  const filled = (draft?.sections || []).filter((s) => (s.body || "").trim());
  if (!filled.length)
    return <EmptyState testid="charter-view-empty" title="No charter generated yet"
                       message="Use “Generate / edit” to fill the template components and generate the family charter." />;
  return (
    <div className="space-y-6" data-testid="charter-view">
      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="bg-gradient-to-br from-plum/10 via-card to-navy/5 px-6 py-10 text-center md:px-12">
          <p className="text-[11px] uppercase tracking-[0.28em] text-plum">Aikyam · Viveka Kartha</p>
          <h2 className="mt-3 font-display text-3xl tracking-tight text-navy md:text-4xl">
            {draft.family_name || "Family"} Family Charter
          </h2>
          <p className="mt-2 text-sm text-foreground/70">{draft.company_name}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {[["Version", draft.version_label], ["Status", draft.status],
              ["Charter date", draft.charter_date], ["Next review", draft.next_review]]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <span key={k} className="rounded-full border border-plum/25 bg-card px-4 py-2 text-[11px] text-navy">
                  <span className="uppercase tracking-[0.14em] text-plum">{k}</span> · {v}
                </span>
              ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {filled.map((s, i) => (
          <article key={s.key} className="panel flex flex-col overflow-hidden"
                   data-testid={`charter-view-${s.key}`}>
            <div className="flex items-center gap-3 border-b border-border bg-plum/5 px-5 py-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-plum font-mono text-[11px] text-white">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display text-lg leading-snug text-navy">{s.title}</h3>
            </div>
            <div className="space-y-2 px-5 py-4 text-sm leading-relaxed text-foreground/85">
              {(s.body || "").split("\n").filter((l) => l.trim()).map((line, idx) =>
                /^[-•*]/.test(line.trim()) ? (
                  <p key={idx} className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-plum" />
                    <span>{line.trim().replace(/^[-•*]\s*/, "")}</span>
                  </p>
                ) : (
                  <p key={idx}>{line}</p>
                )
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default function FamilyCharter() {
  const { tenantId, isPlatformAdmin } = useAuth();
  const [tab, setTab] = useState("charter");
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [att, d] = await Promise.all([
        api.get(`/tenants/${tenantId}/attachments`, {
          params: { scope_type: "pillar_doc", scope_key: "viveka_kartha:family_charter", framework: "viveka_kartha" },
        }),
        api.get(`/tenants/${tenantId}/viveka/charter/draft`),
      ]);
      setFiles(att.data);
      setActive(att.data[0] || null);
      setDraft(d.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const form = new FormData();
    form.append("scope_type", "pillar_doc");
    form.append("scope_key", "viveka_kartha:family_charter");
    form.append("framework", "viveka_kartha");
    form.append("file", file);
    try {
      await api.post(`/tenants/${tenantId}/attachments`, form);
      toast.success("Charter saved as a new version — earlier versions are retained");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeVersion = async (file) => {
    if (!window.confirm(`Delete version ${file.version} (${file.filename})? This is audited.`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/attachments/${file.id}`);
      toast.success(`Version ${file.version} deleted`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const url = active ? fileUrl(`/tenants/${tenantId}/attachments/${active.id}/download`) : null;
  const ext = (active?.filename || "").split(".").pop().toLowerCase();
  const isPdf = ext === "pdf";
  const isWord = ext === "docx" || ext === "doc";
  const previewUrl = active ? fileUrl(`/tenants/${tenantId}/attachments/${active.id}/preview`) : null;
  const sections = useMemo(() => draft?.sections || [], [draft]);
  const canEdit = !!draft?.can_edit;

  return (
    <div className="space-y-8" data-testid="vk-family-charter">
      <Link to="/viveka-kartha" data-testid="charter-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-navy hover:text-deepblue">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-navy text-white"><ArrowLeft size={13} /></span>
        Back to Viveka Kartha
      </Link>

      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="flex items-start gap-5">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-navy text-white">
              <BookBookmark size={26} />
            </div>
            <div>
              <h1 className="font-display text-3xl tracking-tight text-navy">Family charter</h1>
              <p className="mt-1 text-sm text-foreground/75">
                Build the charter on the portal, or upload a version — every earlier version is preserved.
              </p>
            </div>
          </div>
          <label className="cursor-pointer rounded-full bg-navy px-5 py-3 text-center text-[10px] uppercase tracking-[0.16em] text-white transition-colors hover:bg-navy/90">
            <input type="file" className="hidden" data-testid="charter-upload"
                   onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            <UploadSimple size={14} className="mr-2 inline" />
            Upload new charter version
          </label>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-border px-6 py-4 md:px-8">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`charter-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                      tab === t.key ? "bg-plum text-white" : "border border-border text-navy hover:border-plum/50"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "charter" && <CharterView draft={draft} />}

      {tab === "build" && draft && (
        canEdit ? (
          <CharterBuilder draft={draft} setDraft={setDraft} onGenerated={load} />
        ) : (
          <EmptyState testid="charter-readonly" title="Read-only access"
                      message="Only the Kartha / Chairman or an Aikyam Admin can edit the charter. You can still raise points in Family discussion." />
        )
      )}

      {tab === "discussion" && <CharterDiscussion sections={sections} canEdit={canEdit} />}

      {tab === "versions" && (files.length === 0 ? (
        <EmptyState testid="charter-empty" title="No charter attached"
                    message="Generate the charter on the portal or upload a PDF / Word file to start the version history." />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
          <div className="panel overflow-hidden" data-testid="charter-viewer">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
              <div>
                <p className="label-caps">Viewing version {active?.version}</p>
                <p className="mt-1 text-sm font-medium text-navy">{active?.filename}</p>
              </div>
              <a href={url} target="_blank" rel="noreferrer" data-testid="charter-download"
                 className="rounded-full border border-navy/25 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-navy transition-colors hover:bg-navy hover:text-white">
                Download
              </a>
            </div>
            {isPdf ? (
              <iframe src={`${url}&inline=1`} title="Family charter" className="h-[36rem] w-full" data-testid="charter-frame" />
            ) : isWord ? (
              <iframe src={previewUrl} title="Family charter (Word)" className="h-[36rem] w-full bg-card"
                      data-testid="charter-word-frame" />
            ) : (
              <div className="p-10 text-center">
                <Paperclip size={30} className="mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">
                  Preview is available for PDF and Word charters. Use Download to open this file.
                </p>
              </div>
            )}
          </div>

          <div className="panel overflow-hidden" data-testid="charter-history">
            <div className="flex items-center gap-2 border-b border-border px-6 py-4">
              <ClockCounterClockwise size={17} className="text-plum" />
              <h2 className="font-display text-lg text-navy">Evolutionary history</h2>
            </div>
            <div className="divide-y divide-border">
              {files.map((f) => (
                <div key={f.id}
                     className={`flex items-center gap-2 pr-4 transition-colors hover:bg-muted/50 ${
                       active?.id === f.id ? "bg-navy/5" : ""}`}>
                  <button onClick={() => setActive(f)}
                          data-testid={`charter-version-${f.version}`}
                          className="flex flex-1 items-center justify-between gap-4 px-6 py-4 text-left">
                    <div>
                      <p className="text-sm font-medium text-navy">
                        Version {f.version}
                        {f.version === files[0].version && (
                          <span className="ml-2 rounded-full bg-success/20 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-success">
                            Current
                          </span>
                        )}
                      </p>
                      <p className="label-caps mt-1">{f.filename}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[10px] text-muted-foreground">{(f.uploaded_at || "").slice(0, 10)}</p>
                      <p className="text-[10px] text-muted-foreground">{f.uploaded_by_email}</p>
                    </div>
                  </button>
                  {isPlatformAdmin && f.version !== files[0].version && (
                    <button onClick={() => removeVersion(f)}
                            data-testid={`charter-delete-${f.version}`}
                            title="Delete this version"
                            className="shrink-0 rounded-full p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive">
                      <Trash size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
